import "../styles/modal.scss";
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { AnimationLength } from "../utilities/constants";
import { useUpdateState } from "../utilities/utilities";

export function Modal({ className, children, onClose, showModal }) {
  const modalRef = useRef(null); // Create a ref for the modal container
  const [isVisible, setIsVisible] = useState(showModal || true); // Default visibility is controlled by showModal or internal state
  // Close the modal when clicking outside of it
  const handleOutsideClick = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      if (onClose) {
        onClose(); // Notify parent to close the modal
      } else {
        setIsVisible(false); // Internal state management if onClose is not provided
      }
    }
  };

  // Click handler for background
  const handleBackgroundClick = (e) => {
    // If click is directly on the background (not inside modal), close
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      if (onClose) onClose();
    }
  };

  useEffect(() => {
    if (showModal !== undefined) {
      // If showModal is passed, use it to control visibility directly
      setIsVisible(showModal);
    }

    if (isVisible && showModal !== undefined) {
      // Only add the event listener for outside clicks if showModal is passed
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick); // Clean up on unmount or visibility change
    };
  }, [showModal, isVisible]); // Runs when either showModal or isVisible changes

  if (!isVisible) return null; // If modal is not visible, don't render

  return ReactDOM.createPortal(
    <div
      className={"modal-background " + (className || "")}
      onMouseDown={handleBackgroundClick}
      style={{ zIndex: 1000 }}
    >
      <div className="modal" ref={modalRef}>
        <div className="modal-content">{children}</div>
      </div>
    </div>,
    document.body
  );
}

export function Blackout({ show, onClick }) {
  return (
    <FadeContainer
      className="blackout"
      show={show}
      onClick={onClick}
      delay={AnimationLength}
    />
  );
}

export default function FadeContainer({
  children,
  show,
  delay,
  className,
  onClick,
}) {
  const [local, updateLocal] = useUpdateState({});

  if (show) local.lastChildren = children || "";

  useEffect(() => {
    if (local.timer) clearTimeout(local.timer);

    if (show)
      local.timer = setTimeout(
        () => updateLocal({ timer: null, hasFadeIn: true }),
        0
      );
    // Let the element appear with opacity 0 before fading it in
    else
      local.timer = setTimeout(
        () =>
          updateLocal({ timer: null, lastChildren: null, hasFadeIn: false }),
        delay
      ); // The fade animation speed is set in CSS, this just removes the elements once the animation is complete

    return () => clearTimeout(local.timer); // Cancel the timeout if the control is removed from the DOM
  }, [show]);

  let displayChildren = show ? children : local.lastChildren;
  if (displayChildren === null) return null;

  return (
    <div
      className={
        "fade-container " +
        (show && local.hasFadeIn ? "fade-in" : "") +
        (className || "")
      }
      onClick={onClick}
    >
      {displayChildren}
    </div>
  );
}
